import React from "react";
import { Link } from "react-router-dom";
import { Hammer, Heart, Users, ArrowRight, Flame } from "lucide-react";

/**
 * Full-screen Plyx-feed slide that spotlights an African Makers Exhibition
 * showcase. Mounted as part of the swipeable feed via PlyxFeed.
 *
 * Renders identically across mobile + desktop. Tap anywhere → maker detail
 * page; the body image is the dominant visual cue.
 */
const MakerFeedCard = ({ showcase }) => {
  if (!showcase) return null;
  const cover = (showcase.image_urls || [])[0];
  return (
    <div
      className="relative h-full w-full bg-[#0A0A0A] overflow-hidden flex items-center justify-center"
      data-testid={`plyx-feed-maker-${showcase.showcase_id}`}
    >
      {/* Hero image */}
      {cover ? (
        <img
          src={cover}
          alt={showcase.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 80% 10%, #FFB42D 0%, transparent 40%), radial-gradient(circle at 10% 90%, #FF2D7A 0%, transparent 40%), #0A0A0A",
          }}
        />
      )}
      {/* Dark gradient for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/85" />

      {/* Tiny ribbon at the top */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/55 backdrop-blur border border-white/15 text-[10px] uppercase tracking-[0.3em] font-bold text-[#FFB42D]">
        <Hammer className="w-3 h-3" /> Maker Spotlight
      </div>

      {/* Bottom content + CTA */}
      <div className="absolute bottom-0 left-0 right-0 px-5 sm:px-8 pb-24 text-white">
        <div className="max-w-md">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/15 backdrop-blur mb-3">
            {showcase.country}
            {showcase.region ? ` · ${showcase.region}` : ""}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight drop-shadow">
            {showcase.title}
          </h2>
          <p className="mt-2 text-sm text-white/85 line-clamp-3 leading-relaxed">
            {showcase.story?.slice(0, 200)}
            {showcase.story && showcase.story.length > 200 ? "…" : ""}
          </p>
          <div className="mt-4 flex items-center gap-4 text-xs text-white/75">
            <span className="inline-flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> {showcase.sponsor_count} interested
            </span>
            {showcase.funding_goal > 0 && (
              <span className="inline-flex items-center gap-1 text-[#FFB42D] font-semibold">
                <Flame className="w-3.5 h-3.5" /> £{showcase.funding_goal.toLocaleString()} goal
              </span>
            )}
          </div>
          <Link
            to={`/plyx/makers/${showcase.showcase_id}`}
            data-testid={`plyx-feed-maker-cta-${showcase.showcase_id}`}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-[#0A0A0A] text-sm"
            style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
          >
            <Heart className="w-4 h-4" /> View build
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MakerFeedCard;
